import { createApiClient, API_BASE_URL } from './apiClient';

const api = createApiClient();

/**
 * Normalize image URL - convert relative URLs to absolute URLs
 * @param {string} imageUrl - Image URL from backend
 * @returns {string} Absolute image URL
 */
const normalizeImageUrl = (imageUrl) => {
  if (!imageUrl) return null;

  // If already absolute URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // If relative URL, prepend API base URL
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  return `${baseUrl}${path}`;
};

/**
 * Process tag data to add image URL with cache-busting timestamp
 * @param {Object|Array} data - Single tag or array of tags
 * @returns {Object|Array} Processed data with image URLs
 */
const processTagData = (data) => {
  const transformTag = (tag) => {
    const imageUrl = tag.id ? `${API_BASE_URL}/api/tags/${tag.id}/image?t=${Date.now()}` : null;
    return {
      ...tag,
      imageUrl // Add imageUrl with cache-busting timestamp
    };
  };

  if (Array.isArray(data)) {
    return data.map(transformTag);
  }

  if (data && typeof data === 'object') {
    return transformTag(data);
  }

  return data;
};

/**
 * Get all tags with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} params.type - Filter by tag type (CATEGORY, ETIQUETTE, MAIN_DISPLAY)
 * @param {number} params.page - Page number (0-indexed)
 * @param {number} params.size - Page size
 * @returns {Promise} Response with tags data
 */
export const getTags = async (params = {}) => {
  const response = await api.get('/api/tags', { params });
  return processTagData(response.data);
};

/**
 * Get tags by type
 * @param {string} type - Tag type (CATEGORY, ETIQUETTE, MAIN_DISPLAY)
 * @param {Object} params - Additional query parameters
 * @returns {Promise} Response with tags data
 */
export const getTagsByType = async (type, params = {}) => {
  const queryParams = {
    ...params,
    type,
    page: params.page || 0,
    size: params.size || 100,
  };

  const response = await api.get('/api/tags', { params: queryParams });
  return processTagData(response.data);
};

/**
 * Get tag by ID
 * @param {number} id - Tag ID
 * @returns {Promise} Tag data
 */
export const getTagById = async (id) => {
  const response = await api.get(`/api/tags/${id}`);
  return processTagData(response.data);
};

/**
 * Create a new tag
 * @param {Object} tagData - Tag data
 * @param {File} image - Tag image file (optional)
 * @returns {Promise} Created tag data
 */
export const createTag = async (tagData, image = null) => {
  const formData = new FormData();

  // Add tag data as JSON blob with proper content type
  const tagBlob = new Blob([JSON.stringify(tagData)], {
    type: 'application/json'
  });
  formData.append('tag', tagBlob);

  // Add image if provided
  if (image) {
    formData.append('image', image);
  }

  // Interceptor will auto-detect FormData and remove Content-Type header
  const response = await api.post('/api/tags', formData);

  return processTagData(response.data);
};

/**
 * Update an existing tag
 * @param {number} id - Tag ID
 * @param {Object} tagData - Updated tag data
 * @param {File} image - New tag image file (optional)
 * @returns {Promise} Updated tag data
 */
export const updateTag = async (id, tagData, image = null) => {
  const formData = new FormData();

  // Add tag data as JSON blob with proper content type
  const tagBlob = new Blob([JSON.stringify({ ...tagData, id })], {
    type: 'application/json'
  });
  formData.append('tag', tagBlob);

  // Add image only if provided
  if (image) {
    formData.append('image', image);
  }

  // Interceptor will auto-detect FormData and remove Content-Type header
  const response = await api.put(`/api/tags/${id}`, formData);

  return processTagData(response.data);
};

/**
 * Delete a tag
 * @param {number} id - Tag ID
 * @returns {Promise} Success response
 */
export const deleteTag = async (id) => {
  const response = await api.delete(`/api/tags/${id}`);
  return response.data;
};

/**
 * Change tag color (ETIQUETTE type only)
 * @param {number} id - Tag ID
 * @param {string} color - New color (hex format)
 * @returns {Promise} Updated tag data
 */
export const changeTagColor = async (id, color) => {
  const response = await api.post(`/api/tags/${id}/change-color`, { color });
  return response.data;
};

/**
 * Add books to a tag
 * @param {number} tagId - Tag ID
 * @param {number[]} bookIds - Array of book IDs
 * @returns {Promise} Success response
 */
export const addBooksToTag = async (tagId, bookIds) => {
  const response = await api.post(`/api/tags/${tagId}/books/add`, bookIds);
  return response.data;
};

/**
 * Remove books from a tag
 * @param {number} tagId - Tag ID
 * @param {number[]} bookIds - Array of book IDs
 * @returns {Promise} Success response
 */
export const removeBooksFromTag = async (tagId, bookIds) => {
  const response = await api.post(`/api/tags/${tagId}/books/remove`, bookIds);
  return response.data;
};

export default {
  getTags,
  getTagsByType,
  getTagById,
  createTag,
  updateTag,
  deleteTag,
  changeTagColor,
  addBooksToTag,
  removeBooksFromTag,
};
